import { Asset, UpdateAssetDocument, AssetClass, AssetKind, AssetCategory } from "@hydrologiq/gql-experience/graphql"
import { SkeletonText, Box, Skeleton, HStack, VStack, Text, SimpleGrid, GridItem } from "@chakra-ui/react"
import { LocationMarker } from "@hydrologiq/shared/asset/location/map/Map"
import { ScheduleServiceButton } from "@hydrologiq/shared/content/components/Button"
import AssetLogStatus from "@hydrologiq/shared/asset/history/AssetLogStatus"
import { Editable } from "@hydrologiq/shared/chakra/Editable"
import { updateAsset } from "@hydrologiq/assets/state/slices/assetsSlice"
import { useAppDispatch } from "@hydrologiq/shared/state/hooks"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useEffect, useState } from "react"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { formatAssetClass } from "@hydrologiq/shared/asset/PaginatedTable"

/** Format asset category (source) for display */
const formatCategory = (category: AssetCategory | string | null | undefined): string => {
  switch (category) {
    case AssetCategory.Hydrogen:
      return "Hydrogen"
    case AssetCategory.Diesel:
      return "Diesel"
    case AssetCategory.Power:
      return "Power"
    case AssetCategory.Water:
      return "Water"
    case AssetCategory.Waste:
      return "Waste"
    case AssetCategory.Other:
      return "Other"
    default:
      return category ? String(category) : "n/a"
  }
}

/** Format asset kind (subtype) for display */
const formatKind = (kind: AssetKind | string | null | undefined): string => {
  switch (kind) {
    case AssetKind.TubeTrailer:
      return "Tube Trailer"
    case AssetKind.ManifoldCylinderPallet:
      return "Manifold Cylinder Pallet"
    case AssetKind.DynamicStorage:
      return "Dynamic Storage"
    default:
      return kind ? String(kind).replace(/_/g, " ") : "n/a"
  }
}

function AssetInformationField({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <Box py={2}>
      <VStack align="flex-start" gap={0}>
        <Text fontSize="xs" fontWeight="medium" color="gray.500" whiteSpace="nowrap">
          {label}
        </Text>
        <Box>{children}</Box>
      </VStack>
    </Box>
  )
}

const AssetInformation = ({ asset, refetch }: { asset?: Asset | null; refetch: () => void }) => {
  const [mutateAsset, { loading }] = useMutationWithEndpoint(UpdateAssetDocument, {}, "primary")
  const dispatch = useAppDispatch()
  const servicingEnabled = useGetFeature("servicing")

  const [_, setFocusMarker] = useState<LocationMarker | undefined>(undefined)
  useEffect(() => {
    if (asset?.current_location) {
      setFocusMarker({
        location: {
          lat: asset?.current_location.lat ?? 0,
          long: asset?.current_location.long ?? 0,
        },
        title: "Current Location",
      })
    }
  }, [asset])

  const handleUpdate = async (
    name: string | undefined = asset?.name,
    volume: number | null | undefined = asset?.volume,
    min_pressure: number | null | undefined = asset?.min_pressure
  ) => {
    if (!asset || !name) {
      return
    }
    name = name.trim()

    try {
      await mutateAsset({
        variables: {
          id: asset.id,
          name,
          volume: volume || undefined,
          min_pressure: min_pressure || undefined,
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: "Asset", id: asset.id }),
            fields: {
              name: () => name,
              volume: () => volume,
              min_pressure: () => min_pressure,
            },
          })
        },
      })
      const newAsset = { ...asset, name, volume, min_pressure }
      dispatch(updateAsset(newAsset))
    } catch (error) {
      console.error("Failed to update asset:", error)
    }
  }

  let isMonitoringActive = false
  if (asset?.assignments?.active?.length) {
    isMonitoringActive = asset.assignments.active.length > 0
  }

  const showSource = asset?.class === AssetClass.Fuel || asset?.class === AssetClass.Genset || asset?.class === AssetClass.Refueller || asset?.class === AssetClass.Storage
  const showSubtype = asset?.class === AssetClass.Fuel && asset?.category === AssetCategory.Hydrogen
  const showVolume = asset?.class === AssetClass.Fuel || asset?.class === AssetClass.Storage

  return (
    <SimpleGrid columns={{ base: 1, md: servicingEnabled ? 2 : 1 }} gap={3} width="100%">
      {servicingEnabled && (
        <GridItem>
          {!asset ? (
            <Skeleton width={"300px"} height={"32px"} />
          ) : (
            <HStack justifyContent={"flex-start"}>
              <AssetLogStatus
                asset={asset}
                button={true}
                onUpdate={() => {
                  refetch()
                }}
              />
              <ScheduleServiceButton />
            </HStack>
          )}
        </GridItem>
      )}
      <GridItem>
        <SimpleGrid
          columns={{ base: 2, md: 3 }}
          gap={0}
          css={{
            "& > *": {
              borderLeftWidth: "1px",
              borderLeftColor: "var(--chakra-colors-gray-200)",
              paddingLeft: "1rem",
              paddingRight: "1rem",
            },
            "& > *:nth-of-type(2n+1)": {
              borderLeftWidth: "0px",
              paddingLeft: "0",
            },
            "@media (min-width: 48em)": {
              "& > *:nth-of-type(2n+1)": {
                borderLeftWidth: "1px",
                paddingLeft: "1rem",
              },
              "& > *:nth-of-type(3n+1)": {
                borderLeftWidth: "0px",
                paddingLeft: "0",
              },
            },
          }}
        >
          <AssetInformationField label="Name">
            {asset?.name ? (
              <Editable
                variant="hydrologiq"
                defaultValue={asset?.name}
                disabled={loading}
                onValueSave={async (newName: string) => {
                  if (!asset || !newName.trim()) {
                    return
                  }
                  await handleUpdate(newName)
                }}
              />
            ) : (
              <SkeletonText noOfLines={1} width={"200px"} />
            )}
          </AssetInformationField>

          <AssetInformationField label="Type">
            {asset?.class ? (
              <Text>{formatAssetClass(asset.class)}</Text>
            ) : (
              <SkeletonText noOfLines={1} width={"120px"} />
            )}
          </AssetInformationField>

          {showSource && (
            <AssetInformationField label="Source">
              <Text>{formatCategory(asset?.category)}</Text>
            </AssetInformationField>
          )}

          {showSubtype && (
            <AssetInformationField label="Subtype">
              <Text>{formatKind(asset?.kind)}</Text>
            </AssetInformationField>
          )}

          {showVolume && (
            <>
              <AssetInformationField label="Volume (m³)">
                {asset ? (
                  <Editable
                    variant="hydrologiq"
                    defaultValue={asset.volume?.toString() || "n/a"}
                    disabled={loading || isMonitoringActive}
                    disabledHelperText={
                      isMonitoringActive ? "Volume cannot be changed while monitoring is active." : undefined
                    }
                    type="number"
                    step="0.1"
                    onValueSave={async (newValue: string) => {
                      if (!asset) {
                        return
                      }
                      const volume = newValue === "n/a" || !newValue.trim() ? null : parseFloat(newValue)
                      await handleUpdate(asset.name, volume)
                    }}
                  />
                ) : (
                  <SkeletonText noOfLines={1} width={"120px"} />
                )}
              </AssetInformationField>

              <AssetInformationField label="Min Pressure (bar)">
                {asset ? (
                  <Editable
                    variant="hydrologiq"
                    defaultValue={asset.min_pressure?.toString() || "n/a"}
                    disabled={loading || isMonitoringActive}
                    disabledHelperText={
                      isMonitoringActive ? "Minimum pressure cannot be changed while monitoring is active." : undefined
                    }
                    type="number"
                    step="0.1"
                    onValueSave={async (newValue: string) => {
                      if (!asset) {
                        return
                      }
                      const min_pressure = newValue === "n/a" || !newValue.trim() ? null : parseFloat(newValue)
                      await handleUpdate(asset.name, asset.volume, min_pressure)
                    }}
                  />
                ) : (
                  <SkeletonText noOfLines={1} width={"120px"} />
                )}
              </AssetInformationField>
            </>
          )}
        </SimpleGrid>
      </GridItem>
    </SimpleGrid>
  )
}

export default AssetInformation
